// ═══════════════════════════════════════════════════════════════
// TemplateVariablesManagerModal.tsx – a szervezet sablonváltozóinak kezelése önálló ablakban
// (bárhonnan megnyitható, pl. a bejegyzés-szerkesztő változóbeszúrójából)
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Braces, RefreshCw, AlertCircle } from 'lucide-react';
import { ApiClient } from '../../api/client';
import type { TemplateDefinition } from '../../types';
import { TemplateVariablesEditor, type CustomVariable } from './TemplateVariablesEditor';
import { invalidateTemplateVariables } from '../../hooks/useTemplateVariables';

import { ContentSnippetList } from '../common/ContentSnippetList';
import { useAuth } from '../../context/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: number;
}

export const TemplateVariablesManagerModal: React.FC<Props> = ({ isOpen, onClose, tenantId }) => {
  const { role } = useAuth();
  const [def, setDef] = useState<TemplateDefinition | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [customVars, setCustomVars] = useState<CustomVariable[]>([]);
  const [customBaseline, setCustomBaseline] = useState<CustomVariable[]>([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDef(null); setNotice(null); setError(null);
    ApiClient.getTemplateDefinition(tenantId).then((res) => {
      const d = res.ok ? ((res.data ?? res) as TemplateDefinition) : null;
      if (!d?.suggestedVariables) { setError(res.error || 'Nem sikerült betölteni a változókat'); return; }
      setDef(d);
      setValues(d.suggestedVariables); setBaseline(d.suggestedVariables);
      setCustomVars(d.customVariables || []); setCustomBaseline(d.customVariables || []);
    });
  }, [isOpen, tenantId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const save = async () => {
    setSaving(true); setError(null); setNotice(null);
    const res = await ApiClient.saveTemplateVariables(tenantId, values, customVars);
    const p = (res.ok ? (res.data ?? res) : null) as any;
    if (p?.suggestedVariables) {
      setDef((d) => (d ? { ...d, suggestedVariables: p.suggestedVariables, savedKeys: p.savedKeys, customVariables: p.customVariables, variableDefinitions: p.variableDefinitions } : d));
      setValues(p.suggestedVariables); setBaseline(p.suggestedVariables);
      setCustomVars(p.customVariables || []); setCustomBaseline(p.customVariables || []);
      setNotice('Elmentve – a beszúrásnál, a sablon-telepítésnél és a ZIP-ben már ezek az értékek szerepelnek.');
      invalidateTemplateVariables();
    } else {
      setError(res.error || 'Nem sikerült menteni');
    }
    setSaving(false);
  };

  if (!isOpen) return null;
  return createPortal(
    <div data-esc-self className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="tplvars-title"
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col my-auto max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Braces className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <div>
              <h2 id="tplvars-title" className="text-base font-bold text-slate-900 dark:text-white">Sablonváltozók</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">{def?.tenant?.name || 'A szervezet'} – a bejegyzésekbe beszúrható értékek</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg cursor-pointer" aria-label="Bezárás">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {error && (
            <div className="mb-3 p-3 rounded-xl text-xs bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 flex items-center gap-2" role="alert">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {!def ? (
            !error && <div className="py-12 flex justify-center text-slate-400"><RefreshCw className="w-5 h-5 animate-spin" /></div>
          ) : (
            <TemplateVariablesEditor
              definitions={def.variableDefinitions || []}
              customVars={customVars}
              onCustomVarsChange={setCustomVars}
              values={values}
              onChange={(k, v) => setValues((p) => ({ ...p, [k]: v }))}
              onBulkChange={(patch) => setValues((p) => ({ ...p, ...patch }))}
              baseline={baseline}
              customBaseline={customBaseline}
              derived={def.derivedVariables || {}}
              savedKeys={def.savedKeys || []}
              tenantSlug={def.tenant?.slug}
              canSave={Boolean(def.canSaveVariables)}
              saving={saving}
              onSave={save}
              savedNotice={notice}
            />
          )}
          {role === 'superadmin' && <ContentSnippetList tenantId={tenantId} />}
        </div>
      </div>
    </div>,
    document.body
  );
};
